import React from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../../components/context/AuthProvider";

const features = [
  {
    number: "01",
    title: "Living Heroes",
    body: "Heroes carry their history, stats, and fate formulas from session to session — growing richer with every story beat.",
  },
  {
    number: "02",
    title: "Layered Lore",
    body: "Reveal only what each hero knows. Entries have granular visibility, so secrets stay secret until the moment is right.",
  },
  {
    number: "03",
    title: "Woven Campaigns",
    body: "Every NPC, location, and faction is threaded into your chronicle. Cross-reference lore and let the world feel like one coherent whole.",
  },
];

const acts = [
  {
    act: "I",
    title: "Build your world",
    body: "Create heroes, locations, items, and factions. Organise everything into a living library your whole table can explore.",
  },
  {
    act: "II",
    title: "Run your session",
    body: "Reveal lore in real time, roll dice together, track initiative. The table is always in sync.",
  },
  {
    act: "III",
    title: "Watch it evolve",
    body: "Every session deepens the fabric. Entries grow, lore expands, and your chronicle becomes something no one at the table could have written alone.",
  },
];

const Landing: React.FC = () => {
  const { isAuthenticated } = useAuth();

  return (
    <div className="bg-zinc-950 text-white min-h-screen">
      {/* Nav */}
      <header className="fixed top-0 left-0 right-0 z-50 flex justify-between items-center px-8 py-4 border-b border-zinc-900 bg-zinc-950/80 backdrop-blur-sm">
        <div className="flex items-center gap-3 select-none">
          <img src="/logo192.png" alt="RoleWeave" className="w-8 h-8 object-contain" />
          <span className="text-xl font-light tracking-widest">
            Role<span className="text-amber-400 font-normal">Weave</span>
          </span>
        </div>
        <nav className="flex items-center gap-6">
          <Link
            to="/lexicon"
            className="text-zinc-400 hover:text-white text-sm tracking-wide transition-colors duration-200"
          >
            Lexicon
          </Link>
          <Link
            to="/guide"
            className="text-zinc-400 hover:text-white text-sm tracking-wide transition-colors duration-200"
          >
            Guide
          </Link>
          {isAuthenticated ? (
            <Link
              to="/protected/home"
              className="px-5 py-2 border border-amber-400/50 text-amber-400 text-sm tracking-wide hover:bg-amber-400 hover:text-zinc-950 transition-all duration-200"
            >
              Hearth →
            </Link>
          ) : (
            <>
              <Link
                to="/login"
                className="text-zinc-400 hover:text-white text-sm tracking-wide transition-colors duration-200"
              >
                Sign in
              </Link>
              <Link
                to="/register"
                className="px-5 py-2 border border-amber-400/50 text-amber-400 text-sm tracking-wide hover:bg-amber-400 hover:text-zinc-950 transition-all duration-200"
              >
                Get started
              </Link>
            </>
          )}
        </nav>
      </header>

      {/* Hero */}
      <section className="relative min-h-screen flex flex-col items-center justify-center text-center px-6 pt-20">
        {/* Logo */}
        <div className="mb-8 relative">
          <div className="absolute inset-0 rounded-full bg-amber-400/10 blur-2xl scale-150" />
          <img
            src="/logo512.png"
            alt="RoleWeave"
            className="relative w-28 h-28 md:w-40 md:h-40 object-contain drop-shadow-[0_0_32px_rgba(251,191,36,0.3)]"
          />
        </div>
        <p className="text-amber-400 text-xs tracking-[0.45em] uppercase mb-10">
          Where stories are stitched, not rolled
        </p>
        <h1 className="text-7xl md:text-9xl font-thin tracking-tight text-white mb-8 leading-none">
          Role<span className="text-amber-400">Weave</span>
        </h1>
        <p className="text-zinc-300 text-lg md:text-xl max-w-xl mx-auto mb-14 leading-relaxed font-light">
          Share heroes and lore that evolve session&#8209;by&#8209;session, woven into a single
          narrative fabric.
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          {isAuthenticated ? (
            <Link
              to="/protected/home"
              className="px-8 py-3 bg-amber-400 text-zinc-950 text-xs font-semibold tracking-widest uppercase hover:bg-amber-300 transition-colors duration-200"
            >
              Go to Hearth
            </Link>
          ) : (
            <Link
              to="/register"
              className="px-8 py-3 bg-amber-400 text-zinc-950 text-xs font-semibold tracking-widest uppercase hover:bg-amber-300 transition-colors duration-200"
            >
              Begin your story
            </Link>
          )}
          <a
            href="#features"
            className="px-8 py-3 border border-zinc-600 text-zinc-300 text-xs tracking-widest uppercase hover:border-zinc-400 hover:text-white transition-colors duration-200"
          >
            Learn more
          </a>
        </div>

        {/* Scroll line */}
        <div className="absolute bottom-10 left-1/2 -translate-x-1/2 flex flex-col items-center gap-0 opacity-20">
          <div className="w-px h-16 bg-gradient-to-b from-transparent to-zinc-400" />
        </div>
      </section>

      {/* Features */}
      <section id="features" className="py-32 px-6">
        <div className="max-w-5xl mx-auto">
          <p className="text-amber-400/80 text-xs tracking-[0.45em] uppercase text-center mb-3">
            The craft
          </p>
          <h2 className="text-3xl md:text-4xl font-thin text-center text-white mb-20 tracking-wide">
            Every thread matters
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-zinc-800/60">
            {features.map((f) => (
              <div key={f.number} className="bg-zinc-950 p-10 flex flex-col gap-5">
                <span className="text-amber-400/60 text-xs tracking-[0.3em] font-mono">
                  {f.number}
                </span>
                <h3 className="text-base font-normal text-white tracking-wide">{f.title}</h3>
                <p className="text-zinc-400 text-sm leading-relaxed">{f.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="py-32 px-6 border-t border-zinc-900">
        <div className="max-w-3xl mx-auto">
          <p className="text-amber-400/80 text-xs tracking-[0.45em] uppercase text-center mb-3">
            The process
          </p>
          <h2 className="text-3xl md:text-4xl font-thin text-center text-white mb-20 tracking-wide">
            Three acts
          </h2>
          <div className="flex flex-col">
            {acts.map((step) => (
              <div
                key={step.act}
                className="flex gap-10 py-10 border-b border-zinc-900 last:border-0"
              >
                <div className="flex-shrink-0 w-12 pt-0.5">
                  <span className="text-amber-400/60 text-2xl font-thin italic">{step.act}</span>
                </div>
                <div>
                  <h3 className="text-white font-normal mb-3 tracking-wide text-sm">
                    {step.title}
                  </h3>
                  <p className="text-zinc-500 text-sm leading-relaxed">{step.body}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-32 px-6 text-center border-t border-zinc-900">
        <div className="max-w-2xl mx-auto">
          <p className="text-amber-400/80 text-xs tracking-[0.45em] uppercase mb-10">Begin</p>
          <h2 className="text-4xl md:text-5xl font-thin text-white mb-6 leading-tight tracking-wide">
            Your story is waiting
            <br />
            to be woven.
          </h2>
          {isAuthenticated ? (
            <>
              <p className="text-zinc-400 mb-12 text-sm">Welcome back. Your world awaits.</p>
              <Link
                to="/protected/home"
                className="inline-block px-10 py-4 bg-amber-400 text-zinc-950 text-xs font-semibold tracking-widest uppercase hover:bg-amber-300 transition-colors duration-200"
              >
                Continue weaving
              </Link>
            </>
          ) : (
            <>
              <p className="text-zinc-400 mb-12 text-sm">Free to start. No credit card required.</p>
              <Link
                to="/register"
                className="inline-block px-10 py-4 bg-amber-400 text-zinc-950 text-xs font-semibold tracking-widest uppercase hover:bg-amber-300 transition-colors duration-200"
              >
                Start weaving
              </Link>
            </>
          )}
        </div>
      </section>

      {/* Footer */}
      <footer className="py-10 px-8 border-t border-zinc-900 flex flex-col sm:flex-row justify-between items-center gap-4">
        <div className="flex items-center gap-2 select-none">
          <img src="/logo192.png" alt="RoleWeave" className="w-5 h-5 object-contain opacity-70" />
          <span className="text-zinc-500 text-xs tracking-widest">
            Role<span className="text-amber-400/80">Weave</span>
          </span>
        </div>
        <p className="text-zinc-500 text-xs">© 2026 RoleWeave. All rights reserved.</p>
        <div className="flex gap-6">
          <Link
            to="/lexicon"
            className="text-zinc-500 hover:text-zinc-300 text-xs transition-colors duration-200"
          >
            Lexicon
          </Link>
          <Link
            to="/guide"
            className="text-zinc-500 hover:text-zinc-300 text-xs transition-colors duration-200"
          >
            Guide
          </Link>
          {isAuthenticated ? (
            <Link
              to="/protected/home"
              className="text-zinc-500 hover:text-zinc-300 text-xs transition-colors duration-200"
            >
              Hearth
            </Link>
          ) : (
            <>
              <Link
                to="/login"
                className="text-zinc-500 hover:text-zinc-300 text-xs transition-colors duration-200"
              >
                Sign in
              </Link>
              <Link
                to="/register"
                className="text-zinc-500 hover:text-zinc-300 text-xs transition-colors duration-200"
              >
                Register
              </Link>
            </>
          )}
        </div>
      </footer>
    </div>
  );
};

export default Landing;
